import { Link, useNavigate } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import {
  Account,
  ActivityQuery,
  AuthConfig,
  createRowSelection,
  parseDate,
  pluralize,
  queries,
  Test,
  TestsActivity,
  Threat,
  ThreatsActivity,
  useAuthConfig,
} from "@theprelude/core";
import {
  Button,
  CloseIcon,
  FilterIcon,
  IconButton,
  RestartIcon,
  ToggleGroup,
  ToggleItem,
} from "@theprelude/ds";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { shallow } from "zustand/shallow";
import { createQuery } from "@/lib/create-query";
import { getPersonalPercentages, OptionalODPFields } from "@/lib/odp";
import { ActivityQuerySchema } from "@/lib/validators";
import {
  benchmarkColumn,
  checkboxColumn,
  createdColumn,
  defaultHidden,
  getColumnVisibility,
  odpColumns,
  protectedColumn,
  publishedColumn,
  scheduledColumn,
  sourceIdColumn,
  techniqueColumn,
  testIdColumn,
  testNameColumn,
  threatIdColumn,
  threatNameColumn,
} from "../table/columns";
import { TableFilterInput } from "../table/filter-input";
import { useTable } from "../table/hooks";
import { VirtualTable } from "../table/virtual-table";

type FilterTestTableEntry = OptionalODPFields & {
  author: string | undefined;
  account_id: string | undefined;
  testId: string;
  name: string;
  technique?: string;
  created: Date;
  protected?: number;
  benchmark?: number;
  status?: boolean;
};

const testsTableColumnHelper = createColumnHelper<FilterTestTableEntry>();

const testsTableColumns = [
  testsTableColumnHelper.display(checkboxColumn),
  testsTableColumnHelper.accessor("testId", defaultHidden(testIdColumn)),
  testsTableColumnHelper.accessor("name", testNameColumn),
  testsTableColumnHelper.accessor("technique", techniqueColumn),
  testsTableColumnHelper.accessor(
    (row) => row.created.toISOString(),
    createdColumn,
  ),
  ...odpColumns(testsTableColumnHelper),
  testsTableColumnHelper.accessor("benchmark", defaultHidden(benchmarkColumn)),
  testsTableColumnHelper.accessor("status", scheduledColumn),
];

interface FilterThreatTableEntry {
  threatId: string;
  source_id?: string;
  source?: string;
  threat: string;
  tests: string[];
  published?: string;
  protected?: number;
  benchmark?: number;
  status?: boolean;
}

const threatsTableColumnHelper = createColumnHelper<FilterThreatTableEntry>();

const threatsTableColumns = [
  threatsTableColumnHelper.display(checkboxColumn),
  threatsTableColumnHelper.accessor("threatId", defaultHidden(threatIdColumn)),
  threatsTableColumnHelper.accessor("source_id", sourceIdColumn),
  threatsTableColumnHelper.accessor("threat", threatNameColumn),
  threatsTableColumnHelper.accessor("published", publishedColumn),
  threatsTableColumnHelper.accessor("protected", protectedColumn),
  threatsTableColumnHelper.accessor("benchmark", benchmarkColumn),
  threatsTableColumnHelper.accessor("status", scheduledColumn),
];

function getThreatTableData(
  account: Account,
  threats: Threat[],
  threatUsages: ThreatsActivity[],
): FilterThreatTableEntry[] {
  return threats.map((threat) => {
    const usage = threatUsages.find((usage) => usage.id === threat.id);

    const { queue } = account;

    return {
      threatId: threat.id,
      source_id: threat.source_id ?? undefined,
      source: threat.source ?? undefined,
      threat: threat.name,
      tests: threat.tests,
      published: threat.published ?? undefined,
      protected: usage?.protected ?? undefined,
      benchmark: usage?.social ?? undefined,
      status: threat.tombstoned
        ? undefined
        : (queue?.some((q) => q.threat === threat.id) ?? false),
    };
  });
}

function getTestTableData(
  account: Account,
  tests: Test[],
  testUsages: TestsActivity[],
): FilterTestTableEntry[] {
  return tests.map((test) => {
    const usage = testUsages.find((usage) => usage.id === test.id);

    const { queue } = account;

    return {
      account_id: test.account_id ?? undefined,
      author: test.author ?? undefined,
      testId: test.id,
      name: test.name,
      technique: test.technique ?? undefined,
      created: parseDate(test.created),
      protected: usage?.protected ?? undefined,
      ...getPersonalPercentages(usage?.personal),
      benchmark: usage?.social ?? undefined,
      status: test.tombstoned
        ? undefined
        : (queue?.some((q) => q.test === test.id) ?? false),
    };
  });
}

const getFilterTableData = (
  account: Account,
  threats: Threat[],
  tests: Test[],
  threatUsages: ThreatsActivity[],
  testUsages: TestsActivity[],
) => {
  return {
    testTableData: getTestTableData(account, tests, testUsages),
    threatTableData: getThreatTableData(account, threats, threatUsages),
  };
};

const filterTablesQuery = (authConfig: AuthConfig, query: ActivityQuery) =>
  createQuery({
    queries: [
      queries.iam.getAccount(authConfig),
      queries.detect.threats(authConfig),
      queries.detect.tests(authConfig),
      queries.detect.activity.threats(authConfig, {
        start: query.start,
        finish: query.finish,
      }),
      queries.detect.activity.tests(authConfig, {
        start: query.start,
        finish: query.finish,
      }),
    ],
    queryFn: getFilterTableData,
  });

function useRowSelection(initial: string[]) {
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});

  // This useEffect is needed to sync the state between the URL
  // and component when the URL's selection is change
  useEffect(() => {
    setRowSelection(createRowSelection(initial));
  }, [initial]);
  return { rowSelection, setRowSelection };
}

export default function FiltersSection({
  showTable,
  query,
  activity,
}: {
  showTable?: string;
  query: ActivityQuery;
  activity: ActivityQuerySchema;
}) {
  const navigate = useNavigate({ from: "/activity" });
  const authConfig = useAuthConfig();
  const tableQuery = filterTablesQuery(authConfig, query).useQuery();
  const threatTable = tableQuery.data?.threatTableData ?? [];
  const testTable = tableQuery.data?.testTableData ?? [];

  const eventsSelected =
    (activity.tests?.length ?? 0) + (activity.threats?.length ?? 0);

  if (showTable === "threats") {
    return (
      <ThreatsFilterTable
        activity={activity}
        showTable={showTable}
        threatData={threatTable}
        testData={testTable}
        isLoading={tableQuery.isLoading}
        initialRowSelection={activity.threats ?? []}
      />
    );
  }

  if (showTable === "tests") {
    return (
      <TestsFilterTable
        showTable={showTable}
        threatData={threatTable}
        testData={testTable}
        isLoading={tableQuery.isLoading}
        activity={activity}
        initialRowSelection={activity.tests ?? []}
      />
    );
  }

  return (
    <div className="pt-4">
      <div className="flex flex-wrap items-center justify-between gap-4 px-4">
        <div className="flex items-center gap-4">
          <Link to="/activity">
            <Button
              intent="pill"
              icon={<RestartIcon className="text-secondary-foreground" />}
            >
              Reset
            </Button>
          </Link>
        </div>
        <Button
          onClick={() =>
            void navigate({
              search: (prev) => ({
                ...prev,
                show_table: "threats",
              }),
            })
          }
          icon={<FilterIcon className="text-primary-foreground" />}
          intent="secondary"
          className={classNames(
            "border-tertiary-background dark:bg-transparent-black",
            {
              ["bg-highlight"]: eventsSelected > 0,
            },
          )}
          extraIcon={
            eventsSelected > 0 ? (
              <IconButton
                onClick={(ev) => {
                  ev.stopPropagation();
                  void navigate({ search: {} });
                }}
                icon={<CloseIcon />}
              />
            ) : undefined
          }
        >
          {eventsSelected > 0 ? (
            <span>
              {eventsSelected} {pluralize(eventsSelected, "event")} selected
            </span>
          ) : (
            <span>Filter by a specific event</span>
          )}
        </Button>
      </div>
    </div>
  );
}

const ThreatsFilterTable: React.FC<{
  threatData: FilterThreatTableEntry[];
  testData: FilterTestTableEntry[];
  isLoading: boolean;
  showTable: "tests" | "threats";
  activity: ActivityQuerySchema;
  initialRowSelection: string[];
}> = ({
  threatData,
  testData,
  isLoading,
  showTable,
  activity,
  initialRowSelection,
}) => {
  const navigate = useNavigate({ from: "/activity" });
  const { rowSelection, setRowSelection } =
    useRowSelection(initialRowSelection);

  const table = useTable({
    name: "filter-threats",
    data: threatData,
    columns: threatsTableColumns,
    initialState: {
      sorting: [{ desc: true, id: publishedColumn.id }],
      columnVisibility: getColumnVisibility(threatsTableColumns),
    },
    state: {
      rowSelection,
    },
    getRowId: (row) => row.threatId,
    onRowSelectionChange: setRowSelection,
  });

  const isDirty = !shallow(
    table.getSelectedRowModel().rows.map((row) => row.original.threatId),
    activity.threats,
  );

  const selectedIds = table
    .getSelectedRowModel()
    .rows.map((row) => row.original.threatId);

  return (
    <div className="bg-secondary-background pt-4">
      <div className="flex flex-wrap items-center justify-between gap-4 px-4">
        <div className="flex items-center gap-4">
          <Link to="/activity">
            <Button
              intent="pill"
              icon={<RestartIcon className="text-secondary-foreground" />}
            >
              Reset
            </Button>
          </Link>
          <ToggleGroup
            value={showTable}
            onValueChange={(value) => {
              void navigate({
                search: (prev) => ({
                  ...prev,
                  show_table: value,
                  activity: {
                    ...prev.activity,
                    threats: undefined,
                    tests: undefined,
                  },
                }),
              });
            }}
          >
            <ToggleItem value="threats" count={threatData.length}>
              Threats
            </ToggleItem>
            <ToggleItem value="tests" count={testData.length}>
              Tests
            </ToggleItem>
          </ToggleGroup>
        </div>

        <TableFilterInput table={table} />
      </div>
      <section className="mt-4 flex flex-col border-b border-stroke-line px-4 pb-2">
        <VirtualTable
          table={table}
          className="max-h-[40vh]"
          loading={isLoading}
        />
        <footer className="flex justify-end gap-4 pt-4">
          <Button
            intent="secondary"
            onClick={() => {
              void navigate({
                search: (prev) => ({
                  ...prev,
                  show_table: undefined,
                }),
              });
            }}
          >
            Close
          </Button>
          <Button
            disabled={!isDirty}
            onClick={() => {
              void navigate({
                search: (prev) => ({
                  ...prev,
                  activity: { ...prev.activity, threats: selectedIds },
                  show_table: undefined,
                }),
              });
            }}
            intent="primary"
          >
            Apply
          </Button>
        </footer>
      </section>
    </div>
  );
};

const TestsFilterTable: React.FC<{
  threatData: FilterThreatTableEntry[];
  testData: FilterTestTableEntry[];
  isLoading: boolean;
  showTable: "tests" | "threats";
  activity: ActivityQuerySchema;
  initialRowSelection: string[];
}> = ({
  threatData,
  testData,
  isLoading,
  showTable,
  activity,
  initialRowSelection,
}) => {
  const navigate = useNavigate({ from: "/activity" });
  const { rowSelection, setRowSelection } =
    useRowSelection(initialRowSelection);

  const table = useTable({
    name: "filter-tests",
    data: testData,
    columns: testsTableColumns,
    initialState: {
      sorting: [{ desc: true, id: createdColumn.id }],
      columnVisibility: getColumnVisibility(testsTableColumns),
    },
    state: {
      rowSelection,
    },
    onRowSelectionChange: setRowSelection,
    getRowId: (row) => row.testId,
  });

  const isDirty = !shallow(
    table.getSelectedRowModel().rows.map((row) => row.original.testId),
    activity.tests,
  );

  const selectedIds = table
    .getSelectedRowModel()
    .rows.map((row) => row.original.testId);

  return (
    <div className="bg-secondary-background pt-4">
      <div className="flex flex-wrap items-center justify-between gap-4 px-4">
        <div className="flex items-center gap-4">
          <Link to="/activity">
            <Button
              intent="pill"
              icon={<RestartIcon className="text-secondary-foreground" />}
            >
              Reset
            </Button>
          </Link>
          <ToggleGroup
            value={showTable}
            onValueChange={(value) => {
              void navigate({
                search: (prev) => ({
                  ...prev,
                  show_table: value,
                  activity: {
                    ...prev.activity,
                    threats: undefined,
                    tests: undefined,
                  },
                }),
              });
            }}
          >
            <ToggleItem value="threats" count={threatData.length}>
              Threats
            </ToggleItem>
            <ToggleItem value="tests" count={testData.length}>
              Tests
            </ToggleItem>
          </ToggleGroup>
        </div>
        <TableFilterInput table={table} />
      </div>
      <section className="mt-4 flex flex-col border-b border-stroke-line px-4 pb-2">
        <VirtualTable
          table={table}
          className="max-h-[40vh]"
          loading={isLoading}
        />
        <footer className="flex justify-end gap-4 pt-4">
          <Button
            intent="secondary"
            onClick={() => {
              void navigate({
                search: (prev) => ({
                  ...prev,
                  show_table: undefined,
                }),
              });
            }}
          >
            Close
          </Button>
          <Button
            disabled={!isDirty}
            onClick={() => {
              void navigate({
                search: (prev) => ({
                  ...prev,
                  activity: { ...prev.activity, tests: selectedIds },
                  show_table: undefined,
                }),
              });
            }}
            intent="primary"
          >
            Apply
          </Button>
        </footer>
      </section>
    </div>
  );
};
